/**
 * AppsFlyer Analytics Service
 *
 * Integrates AppsFlyer SDK via appsflyer-capacitor-plugin for:
 * - SDK initialization with ATT support (iOS 14.5+)
 * - Customer User ID (CUID) management
 * - AppsFlyer UID retrieval & storage
 * - Screen navigation event tracking (Ofertas, Menús, Mapa)
 * - Content interaction events (likes, clicks on restaurants/offers/menus)
 * - Unified Deep Linking (UDL) for iOS & Android
 *
 * @see https://github.com/AppsFlyerSDK/appsflyer-capacitor-plugin
 */

import { Injectable, inject, signal, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';
import { PlatformService } from './platform.service';
import { AuthService } from './auth.service';

// ─── AppsFlyer Plugin Imports ───────────────────────────────────────────────
// Named exports from the Capacitor plugin (mirrors native DeepLink / DeepLinkResult)
import {
  AppsFlyer,
  type AFInit,
  type AFEvent,
  type OnDeepLink,
  AFConstants,
} from 'appsflyer-capacitor-plugin';

// ─── Event Name Constants ───────────────────────────────────────────────────
// Predefined AppsFlyer event names + custom Gastronauta events

/** Standard AppsFlyer event names */
const AF_EVENTS = {
  CONTENT_VIEW: 'af_content_view',
  LIST_VIEW: 'af_list_view',
  SEARCH: 'af_search',
  ADD_TO_WISHLIST: 'af_add_to_wishlist',
  REMOVE_FROM_WISHLIST: 'af_remove_from_wishlist',
} as const;

/** Custom Gastronauta event names */
export const GA_EVENTS = {
  // Screen navigation
  SCREEN_VIEW: 'ga_screen_view',

  // Content clicks
  OFFER_CLICK: 'ga_offer_click',
  MENU_CLICK: 'ga_menu_click',
  RESTAURANT_CLICK: 'ga_restaurant_click',

  // Likes / favorites
  OFFER_LIKE: 'ga_offer_like',
  OFFER_UNLIKE: 'ga_offer_unlike',
  MENU_LIKE: 'ga_menu_like',
  MENU_UNLIKE: 'ga_menu_unlike',
  RESTAURANT_LIKE: 'ga_restaurant_like',
  RESTAURANT_UNLIKE: 'ga_restaurant_unlike',

  // Category filter
  CATEGORY_FILTER_CLICK: 'ga_category_filter_click',

  // Submit offer funnel
  SUBMIT_OFFER_START: 'ga_submit_offer_start',
  SUBMIT_OFFER_PHOTO_SELECTED: 'ga_submit_offer_photo',
  SUBMIT_OFFER_DETAILS_FILLED: 'ga_submit_offer_details',
  SUBMIT_OFFER_RESTAURANT_SELECTED: 'ga_submit_offer_restaurant',
  SUBMIT_OFFER_SENT: 'ga_submit_offer_sent',

  // Deep link events
  DEEP_LINK_OPENED: 'ga_deep_link_opened',
} as const;

/** Screen name mapping for route → human-readable label */
const SCREEN_NAMES: Record<string, string> = {
  '/offers': 'Ofertas',
  '/menus': 'Menús',
  '/places': 'Mapa',
  '/liked-offers': 'Favoritos',
  '/submit-offer': 'Subir Oferta',
  '/settings': 'Configuración',
  '/admin': 'Admin',
  '/auth': 'Autenticación',
};

// ─── Deep Link Types (mirrors native DeepLinkResult.Status / DeepLink) ───────

/** UDL status from AppsFlyer (NOT_FOUND | ERROR | FAILURE | FOUND). */
export type DeepLinkResultStatus = 'NOT_FOUND' | 'ERROR' | 'FAILURE' | 'FOUND';

/** Payload from the plugin's deepLink object (deep_link_value, is_deferred, etc.). */
export interface DeepLinkPayload {
  deep_link_value?: string | null;
  deep_link_sub1?: string;
  deep_link_sub2?: string;
  deep_link_sub3?: string;
  match_type?: string;
  click_http_referrer?: string;
  /** True when app was installed after user clicked the OneLink (deferred deep link). */
  is_deferred?: boolean;
  [key: string]: unknown;
}

// ─── Deep Link Callback Interface ───────────────────────────────────────────

export interface DeepLinkData {
  deepLinkValue: string | null;
  deepLinkSub1?: string;
  deepLinkSub2?: string;
  deepLinkSub3?: string;
  matchType?: string;
  clickHttpReferrer?: string;
  /** True when this is a deferred deep link (app installed after click). */
  isDeferred?: boolean;
  [key: string]: any;
}

export type DeepLinkHandler = (data: DeepLinkData) => void;

// ─── Service ────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class AppsFlyerService {
  private platform = inject(PlatformService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  /** Whether the SDK has been successfully initialized */
  private _initialized = signal(false);
  readonly initialized = this._initialized.asReadonly();

  /** The AppsFlyer unique device ID */
  private _appsFlyerUID = signal<string | null>(null);
  readonly appsFlyerUID = this._appsFlyerUID.asReadonly();

  /** Registered deep link handler */
  private deepLinkHandler: DeepLinkHandler | null = null;

  /** Track last screen to avoid duplicate events */
  private lastTrackedScreen: string | null = null;

  // ═══════════════════════════════════════════════════════════════════════════
  // SDK INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Initialize the AppsFlyer SDK.
   * Should be called once during app startup (in AppComponent.ngOnInit).
   *
   * On iOS the SDK waits up to 10 s for the ATT prompt result before
   * sending the first launch event, ensuring proper IDFA attribution.
   */
  async initSDK(): Promise<void> {
    // Only run on native platforms (iOS/Android)
    if (!this.platform.isNative()) {
      console.log('[AppsFlyer] Skipping init — not a native platform');
      return;
    }

    if (this._initialized()) {
      console.warn('[AppsFlyer] SDK already initialized');
      return;
    }

    try {
      // 1. Register deep-link listeners BEFORE initSDK (required by plugin)
      this.registerDeepLinkListeners();

      // 2. Build configuration
      const config: AFInit = {
        appID: environment.appsFlyer.appId, // iOS App Store ID
        devKey: environment.appsFlyer.devKey,
        isDebug: environment.appsFlyer.isDebug,
        registerOnDeepLink: true, // Enable Unified Deep Linking (UDL)
        registerConversionListener: true, // Legacy deferred deep link fallback
        registerOnAppOpenAttribution: false, // Superseded by UDL
        minTimeBetweenSessions: 5, // 5 seconds minimum between sessions
        // iOS 14.5+ ATT: wait up to 10 s for user to respond to the ATT prompt
        // before the SDK records the first launch event
        waitForATTUserAuthorization: this.platform.isIOS() ? 10 : undefined,
      };

      console.log('[AppsFlyer] Initializing SDK...', {
        platform: this.platform.getPlatform(),
        isDebug: config.isDebug,
      });

      // 3. Initialize
      const result = await AppsFlyer.initSDK(config);
      console.log('[AppsFlyer] SDK initialized successfully:', result);

      this._initialized.set(true);

      // 4. Retrieve & store the AppsFlyer UID
      await this.fetchAppsFlyerUID();

      // 5. Set Customer User ID if already available
      this.syncCustomerUserId();
    } catch (error) {
      console.error('[AppsFlyer] SDK initialization failed:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CUSTOMER USER ID (CUID) MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Sync the Customer User ID with the current auth state.
   * Call this after login, register, or when auth state changes.
   *
   * - Authenticated users → user.id
   * - Temporal users → sessionId
   */
  syncCustomerUserId(): void {
    if (!this._initialized() || !this.platform.isNative()) return;

    const user = this.authService.currentUser();
    const context = this.authService.userContext?.();

    let cuid: string | null = null;

    if (user?.id) {
      // Authenticated user
      cuid = `user_${user.id}`;
    } else if (context?.sessionId) {
      // Temporal user
      cuid = `session_${context.sessionId}`;
    }

    if (cuid) {
      this.setCustomerUserId(cuid);
    }
  }

  /**
   * Explicitly set the Customer User ID on AppsFlyer.
   * The CUID links AppsFlyer data to your backend user records.
   */
  setCustomerUserId(cuid: string): void {
    if (!this._initialized()) {
      console.warn('[AppsFlyer] Cannot set CUID — SDK not initialized');
      return;
    }

    try {
      AppsFlyer.setCustomerUserId({ cuid });
      console.log('[AppsFlyer] Customer User ID set:', cuid);
    } catch (error) {
      console.error('[AppsFlyer] Failed to set CUID:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // APPSFLYER UID
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Fetch and store the AppsFlyer unique device ID.
   * This ID is useful for server-to-server integrations.
   */
  private async fetchAppsFlyerUID(): Promise<void> {
    try {
      const result = await AppsFlyer.getAppsFlyerUID();
      const uid = result?.uid ?? null;
      this._appsFlyerUID.set(uid);
      console.log('[AppsFlyer] AppsFlyer UID:', uid);
    } catch (error) {
      console.error('[AppsFlyer] Failed to get AppsFlyer UID:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCREEN NAVIGATION TRACKING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Track a screen view event.
   * Called automatically from AppComponent on NavigationEnd.
   *
   * @param url The route URL (e.g., '/offers', '/menus', '/places')
   */
  trackScreenView(url: string): void {
    if (!this._initialized() || !this.platform.isNative()) return;

    // Normalize URL: strip query params and fragments
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Avoid duplicate tracking for same screen
    if (cleanUrl === this.lastTrackedScreen) return;
    this.lastTrackedScreen = cleanUrl;

    // Resolve screen name
    const screenName = this.resolveScreenName(cleanUrl);

    const eventData: AFEvent = {
      eventName: GA_EVENTS.SCREEN_VIEW,
      eventValue: {
        af_content_type: 'screen',
        af_content_id: cleanUrl,
        screen_name: screenName,
        screen_path: cleanUrl,
      },
    };

    this.logEvent(eventData);
  }

  /**
   * Resolve a URL path to a human-readable screen name.
   */
  private resolveScreenName(url: string): string {
    // Exact match first
    if (SCREEN_NAMES[url]) {
      return SCREEN_NAMES[url];
    }

    // Match by prefix (for detail routes like /offers/123)
    for (const [prefix, name] of Object.entries(SCREEN_NAMES)) {
      if (url.startsWith(prefix + '/')) {
        const id = url.replace(prefix + '/', '');
        return `${name} Detalle (${id})`;
      }
    }

    return url;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTENT INTERACTION EVENTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Track when a user clicks on an offer card.
   */
  trackOfferClick(offerId: number, offerTitle?: string, brandName?: string): void {
    this.logEvent({
      eventName: GA_EVENTS.OFFER_CLICK,
      eventValue: {
        af_content_type: 'offer',
        af_content_id: String(offerId),
        af_content: offerTitle ?? '',
        brand_name: brandName ?? '',
      },
    });
  }

  /**
   * Track when a user clicks on a menu card.
   */
  trackMenuClick(menuId: number, menuTitle?: string, brandName?: string): void {
    this.logEvent({
      eventName: GA_EVENTS.MENU_CLICK,
      eventValue: {
        af_content_type: 'menu',
        af_content_id: String(menuId),
        af_content: menuTitle ?? '',
        brand_name: brandName ?? '',
      },
    });
  }

  /**
   * Track when a user clicks on a restaurant / place.
   */
  trackRestaurantClick(restaurantId: number, restaurantName?: string): void {
    this.logEvent({
      eventName: GA_EVENTS.RESTAURANT_CLICK,
      eventValue: {
        af_content_type: 'restaurant',
        af_content_id: String(restaurantId),
        af_content: restaurantName ?? '',
      },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LIKE / FAVORITE EVENTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Track when a user likes/unlikes an offer.
   */
  trackOfferLike(offerId: number, liked: boolean, offerTitle?: string): void {
    this.logEvent({
      eventName: liked ? GA_EVENTS.OFFER_LIKE : GA_EVENTS.OFFER_UNLIKE,
      eventValue: {
        af_content_type: 'offer',
        af_content_id: String(offerId),
        af_content: offerTitle ?? '',
        action: liked ? 'like' : 'unlike',
      },
    });

    // Also log as standard AppsFlyer wishlist event
    if (liked) {
      this.logEvent({
        eventName: AF_EVENTS.ADD_TO_WISHLIST,
        eventValue: {
          af_content_type: 'offer',
          af_content_id: String(offerId),
          af_content: offerTitle ?? '',
        },
      });
    }
  }

  /**
   * Track when a user likes/unlikes a menu.
   */
  trackMenuLike(menuId: number, liked: boolean, menuTitle?: string): void {
    this.logEvent({
      eventName: liked ? GA_EVENTS.MENU_LIKE : GA_EVENTS.MENU_UNLIKE,
      eventValue: {
        af_content_type: 'menu',
        af_content_id: String(menuId),
        af_content: menuTitle ?? '',
        action: liked ? 'like' : 'unlike',
      },
    });

    if (liked) {
      this.logEvent({
        eventName: AF_EVENTS.ADD_TO_WISHLIST,
        eventValue: {
          af_content_type: 'menu',
          af_content_id: String(menuId),
          af_content: menuTitle ?? '',
        },
      });
    }
  }

  /**
   * Track when a user likes/unlikes a restaurant.
   */
  trackRestaurantLike(restaurantId: number, liked: boolean, restaurantName?: string): void {
    this.logEvent({
      eventName: liked ? GA_EVENTS.RESTAURANT_LIKE : GA_EVENTS.RESTAURANT_UNLIKE,
      eventValue: {
        af_content_type: 'restaurant',
        af_content_id: String(restaurantId),
        af_content: restaurantName ?? '',
        action: liked ? 'like' : 'unlike',
      },
    });

    if (liked) {
      this.logEvent({
        eventName: AF_EVENTS.ADD_TO_WISHLIST,
        eventValue: {
          af_content_type: 'restaurant',
          af_content_id: String(restaurantId),
          af_content: restaurantName ?? '',
        },
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SUBMIT OFFER FUNNEL EVENTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Track when a user opens the submit offer screen (funnel entry).
   */
  trackSubmitOfferStart(): void {
    this.logEvent({
      eventName: GA_EVENTS.SUBMIT_OFFER_START,
      eventValue: {
        af_content_type: 'submit_offer',
        funnel_step: 'start',
      },
    });
  }

  /**
   * Track when a user selects/takes a photo (step 1 completed).
   */
  trackSubmitOfferPhotoSelected(): void {
    this.logEvent({
      eventName: GA_EVENTS.SUBMIT_OFFER_PHOTO_SELECTED,
      eventValue: {
        af_content_type: 'submit_offer',
        funnel_step: 'photo_selected',
      },
    });
  }

  /**
   * Track when a user fills in the offer details (step 2 completed).
   */
  trackSubmitOfferDetailsFilled(title: string): void {
    this.logEvent({
      eventName: GA_EVENTS.SUBMIT_OFFER_DETAILS_FILLED,
      eventValue: {
        af_content_type: 'submit_offer',
        funnel_step: 'details_filled',
        af_content: title,
      },
    });
  }

  /**
   * Track when a user selects a restaurant (step 3 completed).
   */
  trackSubmitOfferRestaurantSelected(restaurantId: number, restaurantName: string): void {
    this.logEvent({
      eventName: GA_EVENTS.SUBMIT_OFFER_RESTAURANT_SELECTED,
      eventValue: {
        af_content_type: 'submit_offer',
        funnel_step: 'restaurant_selected',
        af_content_id: String(restaurantId),
        af_content: restaurantName,
      },
    });
  }

  /**
   * Track when a user successfully submits an offer (funnel completion).
   */
  trackSubmitOfferSent(restaurantId: number, restaurantName: string, contactType: string): void {
    this.logEvent({
      eventName: GA_EVENTS.SUBMIT_OFFER_SENT,
      eventValue: {
        af_content_type: 'submit_offer',
        funnel_step: 'sent',
        af_content_id: String(restaurantId),
        af_content: restaurantName,
        contact_type: contactType,
      },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CATEGORY FILTER EVENTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Track when a user taps a category in the filter slider bar.
   *
   * @param categoryName  The display name of the selected category (e.g. "Hamburguesas") or "all"
   * @param categoryId    The numeric category ID, or null when "all" is selected
   * @param context       The screen context: 'ofertas' | 'menus' | 'restaurantes'
   */
  trackCategoryFilterClick(
    categoryName: string,
    categoryId: number | null,
    context: 'ofertas' | 'menus' | 'restaurantes',
  ): void {
    this.logEvent({
      eventName: GA_EVENTS.CATEGORY_FILTER_CLICK,
      eventValue: {
        af_content_type: 'category',
        af_content_id: categoryId != null ? String(categoryId) : 'all',
        af_content: categoryName,
        category_context: context,
      },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UNIFIED DEEP LINKING (UDL)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Register a handler that will be called when a deep link is resolved.
   * Must be called before initSDK() — typically in AppComponent constructor.
   */
  onDeepLink(handler: DeepLinkHandler): void {
    this.deepLinkHandler = handler;
  }

  /**
   * onDeepLinking(result) – called when the SDK has the deep link result.
   * Mirrors native DeepLinkListener.onDeepLinking(DeepLinkResult).
   * - NOT_FOUND: deep link not found
   * - ERROR / FAILURE: log error and return
   * - FOUND: get deep_link_value, optional is_deferred, then route and notify handler
   */
  private onDeepLinking(deepLinkResult: OnDeepLink): void {
    const dlStatus = (deepLinkResult.status ?? '') as DeepLinkResultStatus;

    if (dlStatus === 'NOT_FOUND') {
      console.log('[AppsFlyer] Deep link not found');
      return;
    }
    if (dlStatus === 'ERROR' || dlStatus === 'FAILURE') {
      const dlError = deepLinkResult.error ?? 'Unknown error';
      console.warn('[AppsFlyer] Error getting Deep Link data:', dlError);
      return;
    }

    // dlStatus === 'FOUND'
    console.log('[AppsFlyer] Deep link found');

    let deepLinkObj: Record<string, unknown> | null = null;
    try {
      const raw = deepLinkResult.deepLink;
      if (raw == null) {
        console.warn('[AppsFlyer] DeepLink data came back null');
        return;
      }
      // Plugin may send deepLink as a JSON string (e.g. Android) or as an object (e.g. iOS)
      deepLinkObj =
        typeof raw === 'string'
          ? (JSON.parse(raw) as Record<string, unknown>)
          : (raw as Record<string, unknown>);
    } catch (e) {
      console.warn('[AppsFlyer] DeepLink data invalid or not parseable', e);
      return;
    }

    try {
      const deepLinkDestination =
        (deepLinkObj['deep_link_value'] as string | null | undefined) ??
        (deepLinkObj['deepLinkValue'] as string | null | undefined) ??
        null;
      const isDeferred = Boolean(
        deepLinkObj['is_deferred'] ?? deepLinkObj['isDeferred']
      );

      if (environment.appsFlyer.isDebug) {
        console.log(
          '[AppsFlyer] [DDL] Calling onDeepLinking with:',
          JSON.stringify({
            status: deepLinkResult.status,
            deepLink: deepLinkObj,
          })
        );
      }

      // OneLink params: support both deep_link_sub* and af_sub* (AppsFlyer OneLink)
      const data: DeepLinkData = {
        deepLinkValue: deepLinkDestination,
        deepLinkSub1:
          (deepLinkObj['deep_link_sub1'] as string | undefined) ??
          (deepLinkObj['deepLinkSub1'] as string | undefined) ??
          (deepLinkObj['af_sub1'] as string | undefined),
        deepLinkSub2:
          (deepLinkObj['deep_link_sub2'] as string | undefined) ??
          (deepLinkObj['deepLinkSub2'] as string | undefined) ??
          (deepLinkObj['af_sub2'] as string | undefined),
        deepLinkSub3:
          (deepLinkObj['deep_link_sub3'] as string | undefined) ??
          (deepLinkObj['deepLinkSub3'] as string | undefined) ??
          (deepLinkObj['af_sub3'] as string | undefined),
        matchType:
          (deepLinkObj['match_type'] as string | undefined) ??
          (deepLinkObj['matchType'] as string | undefined),
        clickHttpReferrer:
          (deepLinkObj['click_http_referrer'] as string | undefined) ??
          (deepLinkObj['clickHttpReferrer'] as string | undefined),
        isDeferred,
        ...deepLinkObj,
      };

      this.logEvent({
        eventName: GA_EVENTS.DEEP_LINK_OPENED,
        eventValue: {
          deep_link_value: data.deepLinkValue,
          deep_link_sub1: data.deepLinkSub1 ?? '',
          match_type: data.matchType ?? '',
          is_deferred: data.isDeferred,
        },
      });

      // Route based on deep_link_value (same for direct and deferred deep links)
      this.handleDeepLinkRouting(data);

      if (this.deepLinkHandler) {
        this.deepLinkHandler(data);
      }
    } catch (e) {
      console.warn('[AppsFlyer] DeepLink data came back null', e);
    }
  }

  /**
   * Set up all AppsFlyer deep-link listeners.
   * Called internally before initSDK().
   *
   * UDL callback delegates to onDeepLinking() (mirrors native DeepLinkListener).
   */
  private registerDeepLinkListeners(): void {
    // ── Unified Deep Linking (UDL) – primary mechanism ──
    AppsFlyer.addListener(AFConstants.UDL_CALLBACK, (result: OnDeepLink) => {
      this.ngZone.run(() => this.onDeepLinking(result));
    });

    // ── Conversion Data (deferred deep linking fallback) ──
    AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, (event: any) => {
      console.log('[AppsFlyer] Conversion callback:', JSON.stringify(event));

      this.ngZone.run(() => {
        if (event.callbackName === AFConstants.onConversionDataSuccess) {
          const data = event.data ?? {};
          // Log install UID from CONVERSION (use this for verification, not LAUNCH)
          const uidFromConversion = data.af_uid ?? data.uid ?? null;
          if (uidFromConversion) {
            this._appsFlyerUID.set(uidFromConversion);
            console.log('[AppsFlyer] Install UID from CONVERSION:', uidFromConversion);
          }
          if (
            data.af_status === 'Non-organic' &&
            data.is_first_launch === true
          ) {
            const deepLinkValue = data.deep_link_value;
            if (deepLinkValue) {
              console.log('[AppsFlyer] Deferred deep link:', deepLinkValue);
              this.handleDeepLinkRouting({
                deepLinkValue,
                ...data,
              });
            }
          }
        }
      });
    });
  }

  /**
   * Route the user based on the deep link value.
   * Uses deep_link_value for the content type and deep_link_sub1 for the entity ID.
   *
   * Routing table:
   *   deep_link_value   deep_link_sub1   Routes To
   *   offer             123              /offers/123
   *   menu              456              /menus/456
   *   restaurant        789              /places/789
   *   offers            —                /offers
   *   menus             —                /menus
   *   places            —                /places
   *   favorites         —                /liked-offers
   */
  private handleDeepLinkRouting(data: DeepLinkData): void {
    const value = data.deepLinkValue;
    if (!value) return;

    const id = data.deepLinkSub1;
    let targetRoute: string | null = null;

    switch (value) {
      case 'offer':
        targetRoute = id ? `/offers/${id}` : '/offers';
        break;
      case 'menu':
        targetRoute = id ? `/menus/${id}` : '/menus';
        break;
      case 'restaurant':
        targetRoute = id ? `/places/${id}` : '/places';
        break;
      case 'offers':
        targetRoute = '/offers';
        break;
      case 'menus':
        targetRoute = '/menus';
        break;
      case 'places':
        targetRoute = '/places';
        break;
      case 'favorites':
        targetRoute = '/liked-offers';
        break;
    }

    if (targetRoute) {
      console.log('[AppsFlyer] Navigating to deep link route:', targetRoute);
      this.router.navigateByUrl(targetRoute);
    } else {
      console.warn('[AppsFlyer] Unknown deep_link_value:', value);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GENERIC EVENT LOGGING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Log a custom in-app event to AppsFlyer.
   * Safe to call even if the SDK isn't initialized (events are silently dropped).
   */
  logEvent(event: AFEvent): void {
    if (!this._initialized() || !this.platform.isNative()) return;

    try {
      AppsFlyer.logEvent(event)
        .then(() => {
          if (environment.appsFlyer.isDebug) {
            console.log(`[AppsFlyer] Event logged: ${event.eventName}`, event.eventValue);
          }
        })
        .catch((err: any) => {
          console.error(`[AppsFlyer] Failed to log event ${event.eventName}:`, err);
        });
    } catch (error) {
      console.error(`[AppsFlyer] Error logging event ${event.eventName}:`, error);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // USER INVITE LINK GENERATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Generate a user invite link for sharing offers/menus/restaurants.
   *
   * @param params Link parameters (campaign, channel, custom params)
   * @returns The generated invite link or null
   */
  async generateInviteLink(params: {
    channel: string;
    campaign?: string;
    additionalParams?: Record<string, string>;
  }): Promise<string | null> {
    if (!this._initialized() || !this.platform.isNative()) return null;

    try {
      const result = await AppsFlyer.generateInviteLink({
        channel: params.channel,
        campaign: params.campaign ?? 'user_share',
        addParameters: params.additionalParams ?? {},
      });
      console.log('[AppsFlyer] Invite link generated:', result.link);
      return result.link ?? null;
    } catch (error) {
      console.error('[AppsFlyer] Failed to generate invite link:', error);
      return null;
    }
  }
}
